import { useCallback, useEffect, useMemo, useState } from "react";

import type { ColumnsType, ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { translate } from "app/filters/i18nFilter";
import { AdIntelMetricsUnavailableBanner } from "app/pages/digital-marketing/ad-intelligence/common/banners/AdIntelMetricsUnavailableBanner";
import {
    useDisplayAdvertiserPublisherDataAndParams,
    type DisplayAdvertiserPublisherParams,
} from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useSwitcherLogic } from "../shared/hooks/useSwitcherLogic";
import { SwitcherOption } from "../shared/ShareTypeSwitcher";
import type { AdIntelligenceController } from "../shared/types/types";
import { WidgetBoxWithMarginBottom } from "../StyledComponents";
import type { ISortProps } from "../utils/utils";
import { FIRST_PAGE, getCategoryParam, getGapTranslationKey, PAGE_SIZE_LARGE, getNoDataObject } from "../utils/utils";

import { AdvertisersPublishersTableTop } from "./AdvertisersPublishersTableTop";
import { DisplayAdvertisersPublishersTabs } from "./DisplayAdvertisersPublishersTabs";
import { getAdvertiserPublisherExcelEndpoint, getAdvertiserPublisherCompareExcelEndpoint } from "./endpoints";
import { getColumns } from "./getColumns";
import { getCompareColumns } from "./getCompareColumns";
import { useTableRows } from "./hooks/useTableRows";
import { createHandleSort } from "./utils/tableHandlers";

type DataItem = NonNullable<ReturnType<typeof useTableRows>["data"]>["items"][number];

interface IAdvertisersPublishersProps {
    controller: AdIntelligenceController;
    resource: AdIntelligenceController;
}

const GAP_TOGGLE_MIN_DATE = "2025-05-01";
const GAP_TOGGLE_UNSUPPORTED_COUNTRIES = [804, 643];

export const AdvertisersPublishers = (props: IAdvertisersPublishersProps) => {
    const { key } = useDisplayAdvertiserPublisherDataAndParams();
    // Remounts the table on domain change so gap-toggle/perspective/page/sort state resets
    // before the first render instead of via an effect racing the react-query request.
    return (
        <AdvertisersPublishersTable
            key={key}
            {...props}
        />
    );
};

const AdvertisersPublishersTable = ({
    controller = "advertiser",
    resource = "publisher",
}: IAdvertisersPublishersProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const {
        key = "",
        duration = "",
        country,
        page = FIRST_PAGE,
        includeTerms = "",
        asc,
        sort: sortParam,
        network,
        category,
    } = params;
    const keyDomains = key.split(",").filter(Boolean);
    const isCompareMode = keyDomains.length > 1;
    const [firstDomain] = keyDomains;

    const domains = useMemo(
        () => keyDomains.map((d) => ({ name: d, color: chosenSites.getSiteColor(d) })),
        [keyDomains],
    );

    const durationForTooltip = useMemo(
        () =>
            !isCompareMode
                ? (Object.fromEntries(
                      Object.entries(DurationService.getDurationData(duration).forTooltip as dateForTooltip).map(
                          ([key, item]) => [key, decodeURIComponent(item)],
                      ),
                  ) as dateForTooltip)
                : undefined,
        [duration, isCompareMode],
    );

    // Mirrors backend IsNewDataMode() (AdEntityRequest.cs / AdvertisersBreakdownRequest.cs) —
    // gap filtering only exists in the Firebolt/enhanced query path, so the toggle is hidden
    // for legacy (MySQL) date ranges/countries.
    const isEnhancedDataMode = useMemo(() => {
        const { from } = DurationService.getDurationData(duration).raw;
        return !from.isBefore(GAP_TOGGLE_MIN_DATE) && !GAP_TOGGLE_UNSUPPORTED_COUNTRIES.includes(country);
    }, [duration, country]);

    const [isGapToggleOn, setIsGapToggleOn] = useState(false);
    const [perspectiveDomains, setPerspectiveDomains] = useState<string[]>([firstDomain]);
    const isGapActive = isGapToggleOn && isEnhancedDataMode && isCompareMode;

    const gapSecondaryDomains = useMemo(
        () =>
            domains
                .filter(({ name }) => !perspectiveDomains.includes(name))
                .map(({ name }) => name)
                .join(", "),
        [domains, perspectiveDomains],
    );

    const [currentPage, setCurrentPage] = useState(page);
    const [search, setSearch] = useState<string>(includeTerms);
    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(SwitcherOption.Spend);
    const switcherData = useSwitcherLogic(selectedSwitcherOption);

    const compareSort: ISortProps = useMemo(
        () => ({
            sort: "share",
            asc: asc ? asc === "true" : false,
        }),
        [asc],
    );

    const [singleSort, setSingleSort] = useState<ISortProps>({
        sort: sortParam as string,
        asc: asc ? asc === "true" : false,
    });

    const sort = isCompareMode ? compareSort : singleSort;

    const { isWindow } = DurationService.getDurationData(duration).raw;

    const { data, isLoading, isError } = useTableRows({
        controller,
        resource,
        page: currentPage,
        pageSize: PAGE_SIZE_LARGE,
        isCompareMode,
        search,
        sort,
        gapParams: isGapActive ? { gapDomains: perspectiveDomains, gapMetric: switcherData.switcherKey } : undefined,
    });

    const excelDownloadUrl = useMemo(() => {
        const { from, to, isWindow: isWindowDuration } = DurationService.getDurationData(duration).forAPI;
        const fetchService = DefaultFetchService.getInstance();

        const apiParams = {
            country: country,
            key: isCompareMode ? key : keyDomains,
            from,
            to,
            includeTerms: search,
            isWindow: isWindowDuration,
            network,
            ...getCategoryParam(category),
            ...(isWindowDuration ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
            ...sort,
            ...(isGapActive ? { gapDomain: perspectiveDomains, gapMetric: switcherData.switcherKey } : {}),
        };

        const queryParams = fetchService.requestParams(apiParams);
        const excelEndpoint = isCompareMode
            ? getAdvertiserPublisherCompareExcelEndpoint(controller, resource)
            : getAdvertiserPublisherExcelEndpoint(controller, resource);
        return `${excelEndpoint}?${queryParams}`;
    }, [
        duration,
        country,
        key,
        keyDomains,
        network,
        category,
        search,
        sort,
        controller,
        resource,
        isCompareMode,
        isGapActive,
        perspectiveDomains,
        switcherData.switcherKey,
    ]);

    const handleSort = useMemo(
        () =>
            createHandleSort<DataItem>({
                isCompareMode,
                params,
                setSingleSort,
            }),
        [isCompareMode, params, setSingleSort],
    );

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
        proNavigator.updateQueryParams({ page }, { reload: false });
    }, []);

    const handleSwitcherChange = useCallback(
        (option: SwitcherOption) => {
            setSelectedSwitcherOption(option);
            if (currentPage !== FIRST_PAGE) {
                handlePageChange(FIRST_PAGE);
            }
        },
        [currentPage, handlePageChange],
    );

    useEffect(() => {
        setCurrentPage(page);
    }, [page]);

    useEffect(() => {
        if (isCompareMode) {
            if (data?.hasAdIntelMetrics === false) {
                setSelectedSwitcherOption(SwitcherOption.Visits);
            }
        } else {
            if (
                typeof data?.hasAdIntelMetrics === "boolean" &&
                !data.hasAdIntelMetrics &&
                singleSort.sort !== "visits"
            ) {
                handleSort(!!sortParam)({
                    columnKey: "visits",
                    order: "descend",
                });
            } else if (data?.hasAdIntelMetrics && singleSort.sort !== "spend" && !sortParam) {
                handleSort(false)({
                    columnKey: "spend",
                    order: "descend",
                });
            }
        }
    }, [data?.hasAdIntelMetrics, singleSort.sort, sortParam, isCompareMode, handleSort]);

    const handleFilterChange = useCallback(
        (filter: { id?: string }, param: keyof DisplayAdvertiserPublisherParams) => {
            if (filter?.id) {
                allTrackers.dispatchTrackEvent("Drop Down", "click", `Table/${param}/${filter.id}`);
            }

            if (currentPage !== FIRST_PAGE) {
                setCurrentPage(FIRST_PAGE);
            }

            proNavigator.updateQueryParams(
                {
                    [param]: filter?.id,
                    page: FIRST_PAGE,
                },
                { reload: false },
            );
        },
        [currentPage],
    );

    const columns = useMemo(() => {
        if (data?.hasAdIntelMetrics === undefined) {
            return [];
        }

        if (isCompareMode) {
            return getCompareColumns(
                currentPage,
                PAGE_SIZE_LARGE,
                controller,
                resource,
                data?.totalCount,
                switcherData,
                sort,
                data?.hasAdIntelMetrics,
                handleFilterChange,
            ) as ColumnsType<DataItem>;
        }

        return getColumns(
            handleFilterChange,
            data?.hasAdIntelMetrics,
            controller,
            resource,
            data?.totalCount,
            currentPage,
            PAGE_SIZE_LARGE,
            sort,
            durationForTooltip ?? ({} as dateForTooltip),
            isWindow,
        ) as ColumnsType<DataItem>;
    }, [
        data,
        isCompareMode,
        currentPage,
        controller,
        resource,
        switcherData,
        sort,
        handleFilterChange,
        durationForTooltip,
        isWindow,
    ]);

    const handleSearchInputChange = useCallback(
        (value: string) => {
            proNavigator.updateQueryParams(
                {
                    includeTerms: value,
                    page: FIRST_PAGE,
                },
                { reload: false },
            );
            allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value}`);
            setSearch(value);
            if (currentPage !== FIRST_PAGE) {
                setCurrentPage(FIRST_PAGE);
            }
        },
        [currentPage],
    );

    const handleExpand = useCallback((expanded: boolean, record: DataItem) => {
        const entityUrl = "advertiser" in record ? record.advertiser?.url : record.publisher?.url;
        allTrackers.dispatchTrackEvent(
            "Expand Collapse",
            "click",
            `Table/${expanded ? "Expand" : "Collapse"}/${entityUrl}`,
        );
    }, []);

    const handleGapToggleChange = useCallback((value: boolean) => {
        allTrackers.dispatchTrackEvent("Toggle", "click", value ? "GapFilter/on" : "GapFilter/off");
        setIsGapToggleOn(value);
        setCurrentPage(FIRST_PAGE);
    }, []);

    const handlePerspectivesChange = useCallback((next: string[]) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `GapFilter/perspective/${next.join(",")}`);
        setPerspectiveDomains(next);
        setCurrentPage(FIRST_PAGE);
    }, []);

    const getNoData = (): ITableProps["noData"] => {
        if (isGapActive && data?.items?.length === 0) {
            const isMulti = domains.length - perspectiveDomains.length > 1;
            return {
                title: translate(getGapTranslationKey(controller, "empty.state", isMulti), {
                    secondary: gapSecondaryDomains,
                    primary: perspectiveDomains.join(", "),
                }),
                type: "no-data",
            };
        }
        if (isError || !search.length) {
            return getNoDataObject(isError, controller);
        }
        return {
            title: translate(`ad.intelligence.${controller}.${resource}s.table.search.no.data.title`),
            subtitle: translate(`ad.intelligence.${controller}.${resource}s.table.search.no.data.subtitle`),
            type: "no-data",
        };
    };

    return (
        <Stack gap="24px">
            <DisplayAdvertisersPublishersTabs
                isCompareMode={isCompareMode}
                selectedSwitcherOption={isCompareMode ? selectedSwitcherOption : 0}
                setSelectedSwitcherOption={isCompareMode ? handleSwitcherChange : () => {}}
            />
            <WidgetBoxWithMarginBottom>
                {data?.hasAdIntelMetrics === false && data?.totalCount > 0 ? <AdIntelMetricsUnavailableBanner /> : null}
                <Table<DataItem>
                    key={isCompareMode ? selectedSwitcherOption : "single-mode"}
                    columnConfig={columns}
                    data={data?.items ?? []}
                    isLoading={isLoading}
                    loaderRowCount={13}
                    onSort={isCompareMode ? handleSort(false) : handleSort(true)}
                    localStorageKey={`AdIntelligence_${controller}_${resource}s_${isCompareMode ? "compare" : "single"}`}
                    expandable={{ expandIconColumnIndex: 1, onExpand: handleExpand, indentSize: 24 }}
                    pagination={{
                        pageSize: PAGE_SIZE_LARGE,
                        total: data?.totalCount ?? 0,
                        current: currentPage,
                        onChange: (_page, _pageSize, newPage) => handlePageChange(newPage),
                    }}
                    noData={getNoData()}
                >
                    <AdvertisersPublishersTableTop
                        controller={controller}
                        resource={resource}
                        isCompareMode={isCompareMode}
                        params={params}
                        search={search}
                        excelDownloadUrl={excelDownloadUrl}
                        onSearchChange={handleSearchInputChange}
                        onFilterChange={handleFilterChange}
                        gap={{
                            isActive: isGapActive,
                            isAvailable: isEnhancedDataMode,
                            perspectives: perspectiveDomains,
                            domains,
                            secondaryDomainsLabel: gapSecondaryDomains,
                            onToggle: handleGapToggleChange,
                            onPerspectivesChange: handlePerspectivesChange,
                        }}
                        totalCount={data?.totalCount ?? 0}
                    />
                </Table>
            </WidgetBoxWithMarginBottom>
        </Stack>
    );
};
